import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { useMyProfile, queryKeys } from '@/hooks/api';
import { useUI } from '@/store';
import { Button, Input, Textarea, LoadingScreen } from '@/components/ui';
import { TelegramUtils } from '@/utils/telegram';
import { api } from '@/api';
import { cn, processImageFile } from '@/utils';

const editProfileSchema = z.object({
  photos: z.array(z.any()).min(1, 'Добавьте хотя бы одно фото'),
  name: z.string().min(2, 'Имя должно быть минимум 2 символа'),
  birthdate: z.string().min(1, 'Выберите дату рождения'),
  bio: z.string().min(20, 'Опишите себя подробнее (минимум 20 символов)').max(500, 'Слишком длинное описание'),
  city: z.string().optional(),
  country: z.string().optional(),
  desiredLifestyleId: z.number().min(1, 'Выберите искомый стиль жизни'),
  hobbyIds: z.array(z.number()).optional(),
  meetingGoalIds: z.array(z.number()).optional(),
  lookingFor: z.enum(['male', 'female', 'all']).default('all'),
});

type EditProfileFormData = z.infer<typeof editProfileSchema>;

const EditProfileScreen: React.FC = () => {
  const { setActiveScreen, showToast } = useUI();
  const { data: profile, isLoading: profileLoading } = useMyProfile();
  const queryClient = useQueryClient();
  
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState<Record<number, boolean>>({});
  const [uploadedPhotoUrls, setUploadedPhotoUrls] = useState<string[]>([]);
  const [lifestyleTags, setLifestyleTags] = useState<any[]>([]);
  const [hobbies, setHobbies] = useState<any[]>([]);
  const [meetingGoals, setMeetingGoals] = useState<any[]>([]);
  const [dictionariesLoading, setDictionariesLoading] = useState(true);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = useForm<EditProfileFormData>({
    resolver: zodResolver(editProfileSchema),
    mode: 'onChange',
  });

  const photos = watch('photos') || [];
  const selectedHobbyIds = watch('hobbyIds') || [];
  const selectedMeetingGoalIds = watch('meetingGoalIds') || [];
  const desiredLifestyleId = watch('desiredLifestyleId');
  const lookingFor = watch('lookingFor');
  
  // Debug: log form state
  useEffect(() => {
    console.log('📋 Form state:', {
      isValid,
      errors,
      values: { photos: photos.length, selectedHobbyIds, desiredLifestyleId },
    });
  }, [isValid, errors, photos.length, selectedHobbyIds, desiredLifestyleId]);

  // Load dictionaries
  useEffect(() => {
    const loadDictionaries = async () => {
      try {
        const [lifestyleData, hobbiesData, goalsData] = await Promise.all([
          (api as any).getLifestyleTags(),
          (api as any).getHobbies(),
          (api as any).getMeetingGoals(),
        ]);
        setLifestyleTags(lifestyleData);
        setHobbies(hobbiesData);
        setMeetingGoals(goalsData);
      } catch (error) {
        console.error('Failed to load dictionaries:', error);
        showToast('Не удалось загрузить данные', 'error');
      } finally {
        setDictionariesLoading(false);
      }
    };
    loadDictionaries();
  }, [showToast]);

  // Initialize form with profile data
  useEffect(() => {
    if (profile && lifestyleTags.length > 0) {
      console.log('🔄 Инициализируем форму с данными профиля:', profile);
      
      // Set photos
      setValue('photos', profile.photos, { shouldValidate: true });
      setUploadedPhotoUrls(profile.photos);
      
      // Set basic info
      setValue('name', profile.name, { shouldValidate: true });
      
      // Use real birthdate from profile
      if (profile.birthdate) {
        setValue('birthdate', profile.birthdate, { shouldValidate: true });
      } else {
        // Fallback: calculate from age if birthdate not available
        const birthYear = new Date().getFullYear() - profile.age;
        setValue('birthdate', `${birthYear}-01-01`, { shouldValidate: true });
      }
      
      setValue('bio', profile.bio || '', { shouldValidate: true });
      
      // Set lifestyle tag ID directly from profile
      if (profile.lifestyleTagId) {
        setValue('desiredLifestyleId', profile.lifestyleTagId, { shouldValidate: true });
      } else if (profile.desiredLifestyle) {
        // Fallback: find lifestyle tag ID from name
        const lifestyleTag = lifestyleTags.find(tag => 
          profile.desiredLifestyle?.includes(tag.name)
        );
        if (lifestyleTag) {
          setValue('desiredLifestyleId', lifestyleTag.id, { shouldValidate: true });
        }
      }
      
      // Set hobby IDs from profile
      if (profile.hobbyIds && profile.hobbyIds.length > 0) {
        setValue('hobbyIds', profile.hobbyIds, { shouldValidate: true });
      } else {
        setValue('hobbyIds', [], { shouldValidate: true });
      }
      
      // Set city and country
      if (profile.city) {
        setValue('city', profile.city, { shouldValidate: true });
      }
      if (profile.country) {
        setValue('country', profile.country, { shouldValidate: true });
      }
      
      // Set meeting goals
      if (profile.meetingGoalIds && profile.meetingGoalIds.length > 0) {
        setValue('meetingGoalIds', profile.meetingGoalIds, { shouldValidate: true });
      } else {
        setValue('meetingGoalIds', [], { shouldValidate: true });
      }
      
      // Set looking_for from profile
      if (profile.looking_for) {
        setValue('lookingFor', profile.looking_for, { shouldValidate: true });
      } else {
        setValue('lookingFor', 'all', { shouldValidate: true });
      }
      
      // Trigger validation after all fields are set
      setTimeout(() => {
        trigger();
        console.log('✅ Форма валидирована');
      }, 100);
    }
  }, [profile, lifestyleTags, setValue, trigger]);

  // Configure Telegram buttons
  useEffect(() => {
    TelegramUtils.configureBackButton(true, () => {
      setActiveScreen('profile');
    });
    TelegramUtils.configureMainButton({ visible: false });
  }, [setActiveScreen]);

  const handlePhotoClick = async (index: number) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.style.position = 'fixed';
    input.style.left = '-9999px';
    input.style.top = '0';
    input.multiple = false;
    try {
      document.body.appendChild(input);
    } catch {}
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (file) {
        console.log('📸 Photo selected:', { name: file.name, size: file.size });
        
        // Start upload indicator
        setUploadingPhotos(prev => ({ ...prev, [index]: true }));
        TelegramUtils.haptic.light();
        
        try {
          // Ensure token is set
          const savedToken = localStorage.getItem('auth_token');
          if (savedToken && !(api as any).token) {
            console.log('⚠️ Setting token in API client...');
            api.setToken(savedToken);
          }
          
          // Process image (convert HEIC, compress, validate)
          console.log('🔄 Processing image...');
          const processedFile = await processImageFile(file);
          console.log('✅ Image processed');
          
          // Show preview immediately after processing
          const newPhotos = [...photos];
          newPhotos[index] = processedFile;
          setValue('photos', newPhotos.filter(Boolean), { shouldValidate: true });

          // Upload to server
          console.log('📤 Uploading to server...');
          const result = await api.uploadPhoto(processedFile);
          console.log('✅ Upload successful:', result);
          const newUrls = [...uploadedPhotoUrls];
          newUrls[index] = result.url;
          setUploadedPhotoUrls(newUrls.filter(Boolean));
          TelegramUtils.haptic.success();
        } catch (error: any) {
          console.error('Failed to upload photo:', error);
          TelegramUtils.haptic.error();
          showToast(error.message || 'Ошибка загрузки фото', 'error');
          // Remove photo on error
          const newPhotos = photos.filter((_: any, i: number) => i !== index);
          setValue('photos', newPhotos, { shouldValidate: true });
        } finally {
          setUploadingPhotos(prev => ({ ...prev, [index]: false }));
          try {
            input.value = '';
            document.body.removeChild(input);
          } catch {}
        }
      }
    };
    input.click();
  };

  const handlePhotoRemove = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const newPhotos = [...photos];
    newPhotos.splice(index, 1);
    setValue('photos', newPhotos, { shouldValidate: true });
    
    const newUrls = [...uploadedPhotoUrls];
    newUrls.splice(index, 1);
    setUploadedPhotoUrls(newUrls.filter(Boolean));
    
    TelegramUtils.haptic.light();
  };

  const toggleHobby = (hobbyId: number) => {
    const updated = selectedHobbyIds.includes(hobbyId)
      ? selectedHobbyIds.filter((id: number) => id !== hobbyId)
      : [...selectedHobbyIds, hobbyId];
    setValue('hobbyIds', updated);
    TelegramUtils.haptic.selection();
  };

  const handleDesiredLifestyleSelect = (tagId: number) => {
    setValue('desiredLifestyleId', tagId, { shouldValidate: true });
    TelegramUtils.haptic.selection();
  };

  const handleLookingForSelect = (value: 'male' | 'female' | 'all') => {
    setValue('lookingFor', value, { shouldValidate: true });
    TelegramUtils.haptic.selection();
  };

  const onSubmit = async (formData: EditProfileFormData) => {
    setIsSaving(true);
    
    try {
      console.log('💾 Сохраняем профиль:', formData);

      // 1. Update photos (always send current state)
      if (uploadedPhotoUrls.length > 0) {
        console.log('📸 Обновляем фото:', uploadedPhotoUrls);
        await (api as any).uploadPhotosBulk(uploadedPhotoUrls);
      }

      // 2. Update basic info
      console.log('👤 Обновляем базовую информацию');
      await (api as any).updateBasicInfo({
        first_name: formData.name,
        birthdate: formData.birthdate,
        bio: formData.bio,
        city: formData.city,
        country: formData.country,
        looking_for: formData.lookingFor,
        meeting_goal_ids: formData.meetingGoalIds || [],
      });

      // 3. Update lifestyle
      console.log('🌴 Обновляем lifestyle:', formData.desiredLifestyleId);
      await (api as any).updateLifestyle(formData.desiredLifestyleId);

      // 4. Update hobbies (always send, even if empty to clear)
      console.log('🎯 Обновляем хобби:', formData.hobbyIds);
      await (api as any).updateITInfo({
        hobby_ids: formData.hobbyIds || [],
      });

      console.log('✅ Профиль успешно обновлен');
      
      // Invalidate profile cache to refetch updated data
      console.log('🔄 Обновляем кэш профиля');
      await queryClient.invalidateQueries({ queryKey: queryKeys.myProfile });
      
      TelegramUtils.haptic.success();
      
      // Navigate back to profile
      setActiveScreen('profile');
    } catch (error: any) {
      console.error('❌ Ошибка сохранения профиля:', error);
      TelegramUtils.haptic.error();
      showToast(`Ошибка: ${error.message || 'Попробуйте позже'}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (profileLoading || dictionariesLoading) {
    return <LoadingScreen message="Загружаем ваш профиль..." />;
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-tg-bg">
        <p className="text-white">Профиль не найден</p>
      </div>
    );
  }

  const isUploading = Object.values(uploadingPhotos).some(Boolean);

  // Calculate max date (18 years ago)
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() - 18);
  const maxDateString = maxDate.toISOString().split('T')[0];
  
  // Calculate min date (65 years ago)
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 65);
  const minDateString = minDate.toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-tg-bg safe-area-y pb-32">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <button
          onClick={() => {
            setActiveScreen('profile');
            TelegramUtils.haptic.light();
          }}
          className="p-2 -ml-2 hover:bg-white/5 rounded-xl transition-colors"
        >
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <h1 className="text-xl font-bold text-white absolute left-1/2 transform -translate-x-1/2">
          Редактировать
        </h1>
        
        <div className="w-10" /> {/* Spacer for centering */}
      </div>

      {/* Form Content */}
      <form onSubmit={handleSubmit(onSubmit)} className="px-4 py-6 space-y-8">
        {/* Photos Section */}
        <div>
          <h2 className="text-xl font-bold text-white mb-2">Фото</h2>
          <p className="text-neutral-400 mb-4 text-sm">
            Добавьте до 6 фотографий
          </p>
          
          <div className="grid grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handlePhotoClick(index)}
                className={cn(
                  'aspect-[3/4] rounded-2xl border-2 border-dashed flex items-center justify-center relative overflow-hidden transition-all',
                  {
                    'border-neutral-800 bg-neutral-900/50 hover:bg-neutral-900 hover:border-neutral-700': !photos[index],
                    'border-white/20 bg-neutral-900': photos[index],
                  }
                )}
              >
                {photos[index] ? (
                  <>
                    <img
                      src={photos[index] instanceof File ? URL.createObjectURL(photos[index]) : photos[index]}
                      alt={`Фото ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    
                    {uploadingPhotos[index] && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                    
                    {!uploadingPhotos[index] && (
                      <button
                        type="button"
                        onClick={(e) => handlePhotoRemove(index, e)}
                        className="absolute top-2 right-2 w-6 h-6 bg-black/70 rounded-full flex items-center justify-center hover:bg-black transition-colors z-10"
                      >
                        <span className="text-white text-sm">×</span>
                      </button>
                    )}
                  </>
                ) : (
                  <div className="text-center pointer-events-none">
                    <div className="w-8 h-8 bg-neutral-700 rounded-full flex items-center justify-center mx-auto mb-1">
                      <span className="text-neutral-400 text-lg">+</span>
                    </div>
                    <span className="text-xs text-neutral-500">
                      {index === 0 ? 'Главное' : `${index + 1}`}
                    </span>
                  </div>
                )}
              </button>
            ))}
          </div>

          {isUploading && (
            <div className="mt-3 bg-white/5 border border-white/10 rounded-xl p-3">
              <p className="text-sm text-white/70">⏳ Загружаем фото...</p>
            </div>
          )}
          
          {errors.photos && (
            <p className="text-danger-400 text-sm mt-2">{errors.photos.message}</p>
          )}
        </div>

        {/* Basic Info Section */}
        <div>
          <h2 className="text-xl font-bold text-white mb-4">Основная информация</h2>
          
          <div className="space-y-4">
            <Input
              label="Имя"
              placeholder="Александр"
              {...register('name')}
              error={errors.name?.message}
            />
            
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Дата рождения
              </label>
              <input
                type="date"
                max={maxDateString}
                min={minDateString}
                {...register('birthdate')}
                className="w-full px-4 py-3 text-base bg-tg-secondary-bg border border-neutral-700 rounded-xl placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-white/20 transition-all duration-200 text-white"
              />
              {errors.birthdate && (
                <p className="mt-2 text-sm text-danger-400">
                  {errors.birthdate.message}
                </p>
              )}
            </div>
            
            <Textarea
              label="О себе"
              placeholder="Расскажите о себе..."
              {...register('bio')}
              error={errors.bio?.message}
              rows={5}
            />
          </div>
        </div>

        {/* Location Section */}
        <div>
          <h2 className="text-xl font-bold text-white mb-2">Местоположение</h2>
          <p className="text-neutral-400 mb-4 text-sm">
            Текущее или желаемое для путешествия
          </p>
          
          <div className="space-y-4">
            <Input
              label="Город"
              placeholder="Москва"
              {...register('city')}
              error={errors.city?.message}
            />
            
            <Input
              label="Страна"
              placeholder="Россия"
              {...register('country')}
              error={errors.country?.message}
            />
          </div>
        </div>

        {/* Lifestyle Section */}
        <div>
          <h2 className="text-xl font-bold text-white mb-2">Lifestyle</h2>
          <p className="text-neutral-400 mb-4 text-sm">
            Какой образ жизни ты ищешь?
          </p>
          
          <input type="hidden" {...register('desiredLifestyleId')} />
          
          <div className="space-y-3 mb-6">
            {lifestyleTags.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => handleDesiredLifestyleSelect(tag.id)}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                  desiredLifestyleId === tag.id
                    ? 'border-white bg-white/10 text-white light:border-neutral-800 light:bg-neutral-200 light:text-neutral-900'
                    : 'border-white/20 bg-white/5 text-neutral-400 hover:border-white/40 light:border-neutral-300 light:bg-neutral-100 light:text-neutral-700 light:hover:border-neutral-400'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    desiredLifestyleId === tag.id 
                      ? 'border-white bg-white light:border-neutral-800 light:bg-neutral-800' 
                      : 'border-white/40 light:border-neutral-400'
                  }`}>
                    {desiredLifestyleId === tag.id && (
                      <div className="w-2.5 h-2.5 bg-black rounded-full light:bg-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold">
                      {tag.icon} {tag.name}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
          {errors.desiredLifestyleId && (
            <p className="text-danger-400 text-sm mt-2">{errors.desiredLifestyleId.message}</p>
          )}
        </div>

        {/* Looking For Section */}
        <div>
          <h2 className="text-xl font-bold text-white mb-2">Кого ты ищешь?</h2>
          <p className="text-neutral-400 mb-4 text-sm">
            Укажите ваши предпочтения
          </p>
          
          <input type="hidden" {...register('lookingFor')} />
          
          <div className="space-y-3 mb-6">
            <button
              type="button"
              onClick={() => handleLookingForSelect('male')}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                lookingFor === 'male'
                  ? 'border-white bg-white/10 text-white light:border-neutral-800 light:bg-neutral-200 light:text-neutral-900'
                  : 'border-white/20 bg-white/5 text-neutral-400 hover:border-white/40 light:border-neutral-300 light:bg-neutral-100 light:text-neutral-700 light:hover:border-neutral-400'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  lookingFor === 'male' 
                    ? 'border-white bg-white light:border-neutral-800 light:bg-neutral-800' 
                    : 'border-white/40 light:border-neutral-400'
                }`}>
                  {lookingFor === 'male' && (
                    <div className="w-2.5 h-2.5 bg-black rounded-full light:bg-white" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-semibold">
                    👨 Парня
                  </div>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => handleLookingForSelect('female')}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                lookingFor === 'female'
                  ? 'border-white bg-white/10 text-white light:border-neutral-800 light:bg-neutral-200 light:text-neutral-900'
                  : 'border-white/20 bg-white/5 text-neutral-400 hover:border-white/40 light:border-neutral-300 light:bg-neutral-100 light:text-neutral-700 light:hover:border-neutral-400'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  lookingFor === 'female' 
                    ? 'border-white bg-white light:border-neutral-800 light:bg-neutral-800' 
                    : 'border-white/40 light:border-neutral-400'
                }`}>
                  {lookingFor === 'female' && (
                    <div className="w-2.5 h-2.5 bg-black rounded-full light:bg-white" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-semibold">
                    👩 Девушку
                  </div>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => handleLookingForSelect('all')}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                lookingFor === 'all'
                  ? 'border-white bg-white/10 text-white light:border-neutral-800 light:bg-neutral-200 light:text-neutral-900'
                  : 'border-white/20 bg-white/5 text-neutral-400 hover:border-white/40 light:border-neutral-300 light:bg-neutral-100 light:text-neutral-700 light:hover:border-neutral-400'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  lookingFor === 'all' 
                    ? 'border-white bg-white light:border-neutral-800 light:bg-neutral-800' 
                    : 'border-white/40 light:border-neutral-400'
                }`}>
                  {lookingFor === 'all' && (
                    <div className="w-2.5 h-2.5 bg-black rounded-full light:bg-white" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-semibold">
                    👥 Не важно
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Meeting Goals Section */}
        {meetingGoals.length > 0 && (
          <div>
            <h2 className="text-xl font-bold text-white mb-2">Цели знакомства</h2>
            <p className="text-neutral-400 mb-4 text-sm">
              Что вы ищете • {selectedMeetingGoalIds.length} выбрано
            </p>
            <div className="flex flex-wrap gap-2">
              {meetingGoals.map((goal) => (
                <button
                  key={goal.id}
                  type="button"
                  onClick={() => {
                    const updated = selectedMeetingGoalIds.includes(goal.id)
                      ? selectedMeetingGoalIds.filter((id: number) => id !== goal.id)
                      : [...selectedMeetingGoalIds, goal.id];
                    setValue('meetingGoalIds', updated);
                    TelegramUtils.haptic.selection();
                  }}
                  className={cn(
                    'px-3 py-2 rounded-full text-sm font-medium transition-all',
                    selectedMeetingGoalIds.includes(goal.id)
                      ? 'bg-white text-black'
                      : 'bg-white/10 text-white/70 border border-white/20 hover:bg-white/20'
                  )}
                >
                  {goal.icon} {goal.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Hobbies Section */}
        {hobbies.length > 0 && (
          <div>
            <h2 className="text-xl font-bold text-white mb-2">Хобби</h2>
            <p className="text-neutral-400 mb-4 text-sm">
              Выберите интересы • {selectedHobbyIds.length} выбрано (макс. 10)
            </p>
            <div className="flex flex-wrap gap-2">
              {hobbies.map((hobby) => (
                <button
                  key={hobby.id}
                  type="button"
                  onClick={() => {
                    if (selectedHobbyIds.length < 10 || selectedHobbyIds.includes(hobby.id)) {
                      toggleHobby(hobby.id);
                    }
                  }}
                  className={cn(
                    'px-3 py-2 rounded-full text-sm font-medium transition-all',
                    selectedHobbyIds.includes(hobby.id)
                      ? 'bg-white text-black'
                      : 'bg-white/10 text-white/70 border border-white/20 hover:bg-white/20'
                  )}
                >
                  {hobby.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </form>

      {/* Fixed Bottom Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-tg-bg/95 backdrop-blur-xl border-t border-white/10 safe-area-bottom">
        {/* Debug info */}
        {!isValid && (
          <div className="mb-2 p-2 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-400">
            <p className="font-semibold mb-1">Форма невалидна:</p>
            {Object.keys(errors).map(key => (
              <p key={key}>• {key}: {(errors as any)[key]?.message}</p>
            ))}
          </div>
        )}
        
        <Button
          type="submit"
          onClick={handleSubmit(onSubmit)}
          className="w-full h-14 bg-white text-black hover:bg-neutral-100 font-bold text-base rounded-2xl transition-all active:scale-95 disabled:opacity-50"
          disabled={!isValid || isUploading || isSaving}
          loading={isSaving}
        >
          {isSaving ? 'Сохраняем...' : 'Сохранить изменения'}
        </Button>
      </div>
    </div>
  );
};

export default EditProfileScreen;

